import { Link } from "react-router-dom";
import { Clock, Gift } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { DEALS, HAPPY_HOUR } from "@/lib/site";
import { track } from "@/lib/analytics";
import { DealsGate, useDealsPass } from "@/components/DealSignup";

const today = new Date().getDay();

export const DealCard = ({ d, dark }) => {
  const isToday = d.days.includes(today);
  const locked = !useDealsPass();
  return (
    <div
      data-testid={`deal-card-${d.key}`}
      className={`relative h-full rounded-2xl p-6 transition-transform duration-300 hover:-translate-y-1 sm:p-7 ${isToday ? "bg-brand-lime text-brand-ink" : dark ? "border border-white/10 bg-white/5 text-white" : "border border-neutral-200 bg-white"}`}
    >
      {isToday && <span className="mb-3 inline-block rounded-full bg-brand-ink px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-brand-lime" data-testid="deal-today-badge">Today</span>}
      <p className={`text-xs font-bold uppercase tracking-[0.25em] ${isToday ? "" : dark ? "text-brand-lime" : "text-neutral-500"}`}>{d.day}</p>
      <h3 className={`mt-3 font-display text-3xl font-black leading-none tracking-tight transition-[filter] duration-500 ${locked ? "select-none blur-[7px]" : ""}`} aria-hidden={locked || undefined}>{d.title}</h3>
      {locked && <span className="sr-only">Sign up to unlock this deal</span>}
      <p className={`mt-3 text-sm ${isToday ? "text-brand-ink/75" : dark ? "text-neutral-400" : "text-neutral-500"}`}>{d.note}</p>
    </div>
  );
};

export const HappyHour = ({ dark }) => (
  <div data-testid="happy-hour-banner" className={`flex flex-col gap-4 rounded-2xl p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8 ${dark ? "bg-brand-lime text-brand-ink" : "bg-brand-ink text-white"}`}>
    <div className="flex items-center gap-4">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${dark ? "bg-brand-ink text-brand-lime" : "bg-brand-lime text-brand-ink"}`}><Clock className="h-6 w-6" /></span>
      <div>
        <p className="font-display text-2xl font-black tracking-tight">{HAPPY_HOUR.title}: {HAPPY_HOUR.offer}</p>
        <p className={`text-sm ${dark ? "text-brand-ink/75" : "text-white/80"}`}>{HAPPY_HOUR.when} · Select items</p>
      </div>
    </div>
    <Link to="/shop-all?dtche%5Bcategory%5D=pre-rolls" onClick={() => track("deal_click", { deal: "happy_hour" })} data-testid="happy-hour-shop-button" className="btn-lime self-start sm:self-auto">Shop pre-rolls</Link>
  </div>
);

export const DealsSection = () => (
  <section className="on-dark grain relative bg-brand-ink py-20 text-white sm:py-28" data-testid="home-deals">
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow-lime">Deals Mon – Thu & Sunday</p>
          <h2 className="mt-3 h-section">Special Deals</h2>
          <p className="mt-3 max-w-md text-sm text-neutral-400">Specials on select items Monday to Thursday and Sunday — plus happy hour pre-rolls Monday to Thursday, 4 to 8 PM.</p>
        </div>
        <Link to="/special-deals" data-testid="deals-view-all-button" className="btn-lime"><Gift className="h-4 w-4" /> All deals</Link>
      </Reveal>
      <Reveal className="mb-4"><DealsGate dark /></Reveal>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5 [&>*:last-child]:col-span-2 lg:[&>*:last-child]:col-span-1">
        {DEALS.map((d, i) => <Reveal key={d.key} delay={0.04 * i}><DealCard d={d} dark /></Reveal>)}
      </div>
      <Reveal delay={0.1} className="mt-4"><HappyHour dark /></Reveal>
    </div>
  </section>
);
